import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Banknote,
  BarChart3,
  Bell,
  CalendarRange,
  CheckCheck,
  ContactRound,
  FolderOpen,
  Gauge,
  HardDrive,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  MessagesSquare,
  Network,
  Share2,
  ShieldCheck,
  BookOpen,
  LifeBuoy,
  TrendingUp,
  User,
  Users,
  Workflow,
} from "lucide-react";
import api, { isDriveEmployee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fmtDateTime, RoleBadge } from "@/components/badges";
import { UserAvatar } from "@/components/Avatar";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

function NavItems({ onNavigate, nav }) {
  return (
    <nav className="flex flex-col gap-1" data-testid="main-nav">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          onClick={onNavigate}
          data-testid={`nav-${item.key}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <item.icon className="h-4 w-4 shrink-0" />
          {item.label}
          {item.badge > 0 && (
            <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 font-mono text-[10px] font-bold text-slate-900 [margin-inline-start:auto] ltr:ml-auto rtl:ml-0" data-testid="chat-unread-badge">
              {item.badge > 9 ? "9+" : item.badge}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { projects, selectedId, setSelectedId, project } = useProject();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: notif } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30000,
  });

  const { data: chatUnread } = useQuery({
    queryKey: ["chat-unread", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/chat/unread`)).data,
    enabled: !!selectedId,
    refetchInterval: 15000,
  });

  const { data: companyUnread } = useQuery({
    queryKey: ["chat-unread", "company"],
    queryFn: async () => (await api.get("/company-chat/unread")).data,
    enabled: !!user && isDriveEmployee(user),
    refetchInterval: 15000,
  });

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const nav = [
    { to: "/", key: "dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
    { to: "/progress", key: "progress", label: t("nav.progress"), icon: Gauge },
    ...(project?.can_see_all ? [{ to: "/progress-chart", key: "progress-chart", label: t("nav.progressChart"), icon: TrendingUp }] : []),
    { to: "/files", key: "files-docs", label: t("nav.files"), icon: FolderOpen },
    { to: "/sharing", key: "project-sharing", label: t("nav.sharing"), icon: Share2 },
    { to: "/members", key: "project-members", label: t("nav.members"), icon: Users },
    { to: "/chat", key: "chat", label: t("nav.chat"), icon: MessagesSquare, badge: chatUnread?.unread || 0 },
    ...(isDriveEmployee(user) ? [{ to: "/company-chat", key: "company-chat", label: t("nav.companyChat"), icon: MessageSquare, badge: companyUnread?.unread || 0 }] : []),
    ...(isDriveEmployee(user) ? [{ to: "/drive", key: "pruva-drive", label: t("nav.drive"), icon: HardDrive }] : []),
    ...(isDriveEmployee(user) ? [{ to: "/studio/manpower", key: "studio-manpower", label: t("nav.studioManpower"), icon: ContactRound }] : []),
    ...(isDriveEmployee(user) ? [{ to: "/studio/org-chart", key: "studio-orgchart", label: t("nav.studioOrgChart"), icon: Network }] : []),
    ...(isDriveEmployee(user) ? [{ to: "/studio/gantt", key: "studio-gantt", label: t("nav.studioGantt"), icon: BarChart3 }] : []),
    ...(isDriveEmployee(user) ? [{ to: "/studio/diagrams", key: "studio-diagrams", label: t("nav.studioDiagrams"), icon: Workflow }] : []),
    ...(isDriveEmployee(user) ? [{ to: "/studio/work-program", key: "studio-workprogram", label: t("nav.studioWorkProgram"), icon: CalendarRange }] : []),
    { to: "/rfis", key: "rfis", label: t("nav.rfis"), icon: MessageSquare },
    ...(project?.can_view_payments ? [{ to: "/payments", key: "payments-ipc", label: t("nav.payments"), icon: Banknote }] : []),
    ...(user?.is_super_admin || user?.is_admin ? [{ to: "/admin", key: "admin", label: t("nav.admin"), icon: ShieldCheck }] : []),
    { to: "/support", key: "support", label: t("nav.support"), icon: LifeBuoy },
    { to: "/guide", key: "user-guide", label: t("nav.guide"), icon: BookOpen },
  ];

  const unread = notif?.unread || 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-900/95 text-white shadow-md backdrop-blur-xl">
        <div className="flex items-center gap-3 px-4 py-3 lg:px-8">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-slate-800 lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-white p-4">
              <div className="mb-4 flex items-center gap-2 px-2 pt-2">
                <img src="/pruva-logo-full.png" alt="Pruva Swimming Pools" className="h-10 w-auto" />
              </div>
              <NavItems nav={nav} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2" data-testid="app-logo">
            <img src="/pruva-logo-icon.png" alt="Pruva" className="h-7 w-auto" />
            <div className="leading-tight">
              <div className="font-display text-base font-extrabold tracking-tight">PRUVA</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Project Tracking</div>
            </div>
          </div>

          <div className="ml-4 hidden sm:block">
            <Select value={selectedId || ""} onValueChange={setSelectedId}>
              <SelectTrigger className="h-9 w-64 border-slate-700 bg-slate-800 text-white" data-testid="project-switcher">
                <SelectValue placeholder={t("common.selectProject")} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`project-option-${p.id}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="[margin-inline-start:auto] flex items-center gap-2">
            <LanguageSwitcher dark />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative text-white hover:bg-slate-800" data-testid="notification-bell">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 font-mono text-[10px] font-bold text-slate-900" data-testid="notification-unread-count">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0">
                <div className="flex items-center justify-between border-b px-4 py-3">
                  <span className="font-display text-sm font-semibold">{t("common.notifications")}</span>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead} data-testid="mark-all-read-button">
                    <CheckCheck className="mr-1 h-3.5 w-3.5" /> {t("common.markAllRead")}
                  </Button>
                </div>
                <ScrollArea className="max-h-96">
                  {(!notif?.items || notif.items.length === 0) && (
                    <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notifications-empty">{t("common.noNotifications")}</div>
                  )}
                  {notif?.items?.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => markRead(n.id)}
                      data-testid={`notification-item-${n.id}`}
                      className={`block w-full border-b px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "opacity-60" : "bg-sky-50/60"}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-900">{n.title}</span>
                        {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-sky-500" />}
                      </div>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{n.body}</p>
                      <span className="mt-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">{fmtDateTime(n.created_at)}</span>
                    </button>
                  ))}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-white hover:bg-slate-800" data-testid="user-menu-trigger">
                  <UserAvatar user={user} />
                  <span className="hidden text-sm md:block">{user?.name || user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal text-slate-500">{user?.email}</div>
                  <div className="mt-1.5">{user?.is_super_admin ? <RoleBadge role="Super Admin" data-testid="user-role-badge" /> : user?.is_admin ? <RoleBadge role="Admin" data-testid="user-role-badge" /> : null}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="my-profile-link">
                  <User className="mr-2 h-4 w-4" /> {t("common.myProfile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  data-testid="logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="px-4 pb-3 sm:hidden">
          <Select value={selectedId || ""} onValueChange={setSelectedId}>
            <SelectTrigger className="h-9 w-full border-slate-700 bg-slate-800 text-white" data-testid="project-switcher-mobile">
              <SelectValue placeholder={t("common.selectProject")} />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="flex">
        <aside className="sticky top-[61px] hidden h-[calc(100vh-61px)] w-60 shrink-0 border-r bg-white p-4 lg:block">
          <NavItems nav={nav} />
        </aside>
        <main className="min-w-0 flex-1 px-4 py-6 lg:px-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
